<script>
import {
  iAlertMessage,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iTable,
  iTableRow,
  iTableSortButton,
} from '@inscribe/signal';
import infoTooltip from '@/shared/components/InfoTooltip';
import { toCurrency } from '@/utils/toCurrency';
import { mapState } from 'vuex';
import { isErrorResponse } from '@/V2/stores/CreditStore';
import moment from 'moment';

const TABLE_SELECTOR = '.credit-monthly-view__table';

const FALLBACK_ITEM = {
  year_month: null,
  bank_name: null,
  bank_account_last_4: null,
  monthly_starting_balance: null,
  monthly_ending_balance: null,
  monthly_actual_deposit_amount: null,
  monthly_count_actual_deposits: null,
  monthly_debit_amount: null,
  monthly_gross_deposit_amount: null,
  monthly_count_gross_deposits: null,
  monthly_average_daily_balance: null,
  monthly_count_negative_balance_days: null,
  monthly_loan_total_disbursements: null,
  monthly_loan_total_repayments: null,
  monthly_transfers_in: null,
  monthly_transfers_out: null,
  loans: null,
};

export default {
  name: 'CreditMonthlyTableView',
  components: {
    iAlertMessage,
    iButton,
    infoTooltip,
    iTable,
    iTableRow,
    iTableSortButton,
  },
  data() {
    return {
      moment,
      toCurrency,
      sortType: 'user',
      sortOrder: 'asc',
      stickyHeader: true,
      visilibleColumns: [],
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
    }),
    creditInsightsByMonthData() {
      const sortIndex = this.sortOrder === 'asc' ? +1 : -1;
      if (this.credit?.creditInsightsByMonth) {
        const data = [...this.credit.creditInsightsByMonth];
        return data.sort((d1, d2) => {
          return moment(`${d1.year_month}-01`).format('MMM YYYY') >
            moment(`${d2.year_month}-01`).format('MMM YYYY')
            ? 1 * sortIndex
            : -1 * sortIndex;
        });
      } else {
        return undefined;
      }
    },
    isLoading() {
      return this.creditInsightsByMonthData === undefined;
    },
    isError() {
      if (this.isLoading === false) {
        return isErrorResponse(this.creditInsightsByMonthData);
      }
      return false;
    },
    items() {
      if (this.isLoading) {
        return [FALLBACK_ITEM];
      } else {
        return this.creditInsightsByMonthData;
      }
    },
  },
  watch: {
    items() {
      this.$nextTick(() => {
        this.manageScrollEvent('remove');
        this.manageScrollEvent('add');
        this.getVisibleCells();
      });
    },
  },
  unmounted() {
    this.manageScrollEvent('remove');
  },
  methods: {
    onOrderChange(field, order) {
      this.sortType = field;
      this.sortOrder = order;
    },
    formatAsCurrency(value) {
      return value === null ? '-' : `${toCurrency(value, null, 2)}`;
    },
    formatDefault(value) {
      return value === null || value === '' ? '-' : value;
    },
    scrollLeft() {
      const tableElement = document.querySelector(TABLE_SELECTOR);
      tableElement.scrollLeft -= 100; // adjust the value as needed
      this.getVisibleCells();
    },
    scrollRight() {
      const tableElement = document.querySelector(TABLE_SELECTOR);
      tableElement.scrollLeft += 100; //
      this.getVisibleCells();
    },
    getVisibleCells() {
      const containerElement = document.querySelector(
        '.credit-monthly-view__table',
      );
      const tableElement = document.querySelectorAll('.i-table');
      if (!containerElement || !tableElement) {
        return;
      }
      const firstRow = tableElement[0].querySelector(
        '.i-table-row:not(.header)',
      );
      if (!firstRow) {
        return;
      }
      const cells = firstRow.querySelectorAll('div');
      const containerRect = containerElement.getBoundingClientRect();
      this.visilibleColumns = [];

      cells.forEach((cell, index) => {
        const cellRect = cell.getBoundingClientRect();
        if (
          cellRect.right <= containerRect.right &&
          cellRect.left >= containerRect.left
        ) {
          this.visilibleColumns.push(index);
        }
      });
    },
    manageScrollEvent(action) {
      const table = document.querySelector('.credit-monthly-view__table');
      if (action === 'add') {
        table && table.addEventListener('scroll', this.getVisibleCells);
      } else if (action === 'remove') {
        table && table.removeEventListener('scroll', this.getVisibleCells);
      }
    },
    isColumnVisible(index) {
      return this.visilibleColumns.includes(index);
    },
  },
};
</script>

<template>
  <div
    class="credit-monthly-view i-mt-13 i-radius-xl"
    data-testid="credit-monthly-view"
  >
    <div
      class="credit-monthly-view__header d-flex justify-content-between i-p-10"
    >
      <h3 class="i-text-neutral-120 i-text-subheading-1">
        Month by Month Insights View
      </h3>
      <div v-if="!isLoading" class="d-flex align-items-center">
        <i-button
          left-icon="small-arrows-left-small"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          class="i-mr-1"
          @button-click="scrollLeft"
        />
        <div class="credit-monthly-view__dots d-flex align-items-center">
          <span
            v-for="(dot, index) in [
              0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
            ]"
            :key="index"
            class="dot"
            :class="{ visible: isColumnVisible(index) }"
          ></span>
        </div>

        <i-button
          left-icon="small-arrows-right-small"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          class="i-ml-1"
          @button-click="scrollRight"
        />
      </div>
    </div>
    <div ref="creditMonthlyTable" class="credit-monthly-view__table">
      <i-table :loading="isLoading" data-testid="credit-insights-monthly-table">
        <!-- header -->
        <template #header>
          <i-table-row class="high" type="header">
            <template #cells>
              <div>
                <i-table-sort-button
                  label="Month"
                  @order-change="(order) => onOrderChange('month', order)"
                ></i-table-sort-button>
              </div>
              <div>Bank Name</div>
              <div>Account Number (last 4 digits)</div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Starting Balance
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Beginning Balance"
                    subtitle="The earliest balance available in the data for the given month"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Ending Balance
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Ending Balance"
                    subtitle="The latest balance available in the data for the given month"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Total Income Deposits
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Actual Deposit Amount"
                    subtitle="The sum of all income transaction amounts"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Income Deposits Count
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Actual Deposits Count"
                    subtitle="The count of all income transactions"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Total Debits
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Pulls"
                    subtitle="Total amount debited out of the account(s)"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Total Credits
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Gross Deposit Amount"
                    subtitle="The sum of all incoming transaction amounts"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Credit Transactions Count
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Gross Deposits Count"
                    subtitle="The count of all incoming transactions"
                  ></info-tooltip>
                </div>
              </div>

              <div>Avg. Daily Balance</div>
              <div>Negative Balance Days</div>
              <div>
                <div class="d-flex i-gap-2">
                  Loan Disbursements
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Fundings"
                    subtitle="The total incoming loan amounts for the given month"
                  ></info-tooltip>
                </div>
              </div>

              <div>Loan Repayments</div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Transfers In
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Bank Transfers In"
                    subtitle="Total bank transfers incoming"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Transfers Out
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Bank Transfers Out"
                    subtitle="Total bank transfers outgoing"
                  ></info-tooltip>
                </div>
              </div>
              <div>
                <div class="d-flex i-gap-2 align-items-center">
                  Loans
                  <info-tooltip
                    icon-class="i-text-neutral-90"
                    title="Loans"
                    subtitle="Information about each loan found in the transaction data"
                  ></info-tooltip>
                </div>
              </div>
            </template>
          </i-table-row>
        </template>

        <!-- body -->
        <template #rows>
          <i-table-row v-for="item in items" :key="item.id" class="high">
            <template #cells>
              <!-- month -->
              <div>
                {{
                  item.year_month
                    ? moment(`${item.year_month}-01`).format('MMM YYYY')
                    : '-'
                }}
              </div>

              <!-- Bank name -->
              <div class="text-nowrap">
                {{ formatDefault(item.bank_name) }}
              </div>

              <!-- Account number - last four digits -->
              <div>{{ item.bank_account_last_4 }}</div>

              <!-- starting balance -->
              <div>{{ formatAsCurrency(item.monthly_starting_balance) }}</div>

              <!-- ending balance -->
              <div>{{ formatAsCurrency(item.monthly_ending_balance) }}</div>

              <!-- Total Income Deposits - monthly_actual_deposit_amount -->
              <div>
                {{ formatAsCurrency(item.monthly_actual_deposit_amount) }}
              </div>

              <!-- Income Deposits Count - monthly_count_actual_deposits -->
              <div>{{ item.monthly_count_actual_deposits }}</div>

              <!-- Total debits - monthly_debit_amount -->
              <div>{{ formatAsCurrency(item.monthly_debit_amount) }}</div>

              <!-- Total credits - monthly_gross_deposit_amount -->
              <div>
                {{ formatAsCurrency(item.monthly_gross_deposit_amount) }}
              </div>

              <!-- Credit Transactions Count - monthly_count_gross_deposits -->
              <div>{{ item.monthly_count_gross_deposits }}</div>

              <!-- Avg Daily Balance - monthly_average_daily_balance -->
              <div>
                {{ formatAsCurrency(item.monthly_average_daily_balance) }}
              </div>

              <!-- Negative Balance days - monthly_count_negative_balance_days -->
              <div>
                {{ item.monthly_count_negative_balance_days }}
              </div>

              <!-- loan disbursements -->
              <div>
                {{ formatAsCurrency(item.monthly_loan_total_disbursements) }}
              </div>

              <!-- loan repayments -->
              <div>
                {{ formatAsCurrency(item.monthly_loan_total_repayments) }}
              </div>

              <!-- transfers in -->
              <div>{{ formatAsCurrency(item.monthly_transfers_in) }}</div>

              <!-- transfers out -->
              <div>{{ formatAsCurrency(item.monthly_transfers_out) }}</div>

              <!-- loans -->
              <div class="full-content">
                {{ item.loans?.join() || '' }}
              </div>
            </template>
          </i-table-row>
        </template>
      </i-table>
    </div>
    <div
      v-if="isError"
      class="d-flex flex-column align-items-center i-p-12"
      data-testid="credit-monthly-table-error"
    >
      <i-alert-message
        class="credit-monthly-view__error-message"
        type="error"
        heading="Error loading data"
        size="md"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.credit-monthly-view {
  overflow: hidden;
  border: 1px solid $light-neutral-60;

  &__dots {
    .dot {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: $light-neutral-80;
      margin: 0 2px;
      &.visible {
        background: $light-neutral-110;
      }
    }
  }
  &__table {
    overflow: scroll;
    max-height: 300px;
    .i-table {
      width: 4200px;
    }
  }
  &__error-message {
    width: 300px;
  }
}
</style>
